import type { ColumnDef } from "@tanstack/react-table";
import type { ProductBrand } from "@/types";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui";

interface BrandColumnsOptions {
  onEdit: (brand: ProductBrand) => void;
  onDelete: (brand: ProductBrand) => void;
  // The brand wire shape carries source_id only (no denormalized network name) — the page builds
  // this map from useNetworkOptions() so the column can render a human-readable network.
  networkNames: Map<number, string>;
}

export function getBrandColumns({
  onEdit,
  onDelete,
  networkNames,
}: BrandColumnsOptions): ColumnDef<ProductBrand>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="max-w-[200px] truncate font-medium">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "external_id",
      header: "External ID",
      cell: ({ row }) => <span className="font-mono text-sm">{row.getValue("external_id")}</span>,
    },
    {
      accessorKey: "source_id",
      header: "Network",
      cell: ({ row }) => {
        const sourceId = row.getValue("source_id") as number;
        return (
          <span className="text-muted-foreground">
            {networkNames.get(sourceId) ?? String(sourceId)}
          </span>
        );
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ row }) => {
        const active = row.getValue("active") as boolean;
        return (
          <Badge variant={active ? "default" : "destructive"}>
            {active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "created_date",
      header: "Created",
      cell: ({ row }) => {
        const date = row.getValue("created_date") as string;
        return <span className="text-muted-foreground">{new Date(date).toLocaleDateString()}</span>;
      },
    },
    createActionsColumn<ProductBrand>({ onEdit, onDelete }),
  ];
}
